import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Lock, ShoppingBag, ChevronRight, Loader2, Truck, Zap, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { SHIPPING_OPTIONS, FREE_SHIPPING_THRESHOLD, formatPrice } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayPalMark = () => (
  <span className="font-bold italic"><span className="text-[#003087]">Pay</span><span className="text-[#0070E0]">Pal</span></span>
);

const Checkout = () => {
  const { cartLines, subtotal, count, promo, setPromo, discount } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shipping, setShipping] = useState("standard");
  const [method, setMethod] = useState("card");
  const [promoInput, setPromoInput] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", phone: "",
    address: "", city: "", zip: "", country: "France",
  });

  const shippingFee = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_OPTIONS.find((s) => s.id === shipping).price;
  const total = subtotal - discount + shippingFee;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, {
        code: promoInput.trim(),
        subtotal_cents: Math.round(subtotal * 100),
      });
      setPromo({ code: res.data.code, percent: res.data.percent });
      toast.success(`Code ${res.data.code} appliqué ✦`);
    } catch {
      toast.error("Code promo invalide ou expiré");
    } finally {
      setPromoLoading(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    if (!form.email.includes("@")) return "Merci de saisir un email valide";
    if (!form.first_name.trim() || !form.last_name.trim()) return "Merci d'indiquer vos nom et prénom";
    if (form.phone.trim().length < 6) return "Merci d'indiquer un numéro de téléphone valide";
    if (form.address.trim().length < 3 || !form.city.trim() || form.zip.trim().length < 2) return "Merci de compléter votre adresse de livraison";
    return null;
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (cartLines.length === 0) return;
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: cartLines.map((l) => ({ lookup_key: l.variant.id, quantity: l.qty })),
        shipping,
        payment_method: method,
        origin_url: window.location.origin,
        customer: form,
        promo_code: promo ? promo.code : null,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.success_url) {
        window.location.href = res.data.success_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue, réessayez");
      setLoading(false);
    }
  };

  if (cartLines.length === 0) {
    return (
      <div data-testid="checkout-empty" className="pt-32 pb-24 min-h-screen bg-cream flex flex-col items-center justify-center text-center px-5">
        <div className="w-20 h-20 rounded-full bg-blush flex items-center justify-center mb-6">
          <ShoppingBag size={28} className="text-rose-gold" />
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl text-onyx">Votre panier est vide</h1>
        <p className="mt-3 text-muted-warm text-sm">Ajoutez votre masque bio-collagène avant de passer commande.</p>
        <Link to="/produit" data-testid="checkout-empty-cta" className="mt-8 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-semibold hover:bg-onyx transition-colors">
          Découvrir le masque
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-28 sm:pt-36 pb-24 bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex items-center gap-2 mb-10">
          <Lock size={16} className="text-[#2E6B4F]" />
          <h1 className="font-serif text-3xl sm:text-4xl text-onyx">Finaliser ma commande</h1>
          <span data-testid="checkout-item-count" className="ml-2 font-mono text-xs text-muted-warm">({count} article{count > 1 ? "s" : ""})</span>
        </div>
        <form onSubmit={handlePay} className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 space-y-8">
            {/* Step 1 */}
            <section className="bg-white rounded-3xl border border-blush-deep p-6 sm:p-8" data-testid="checkout-step-info">
              <p className="font-mono text-xs tracking-widest uppercase text-terracotta mb-6 flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-terracotta text-cream flex items-center justify-center text-[11px]">1</span>
                Vos informations
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="checkout-input-email" type="email" required placeholder="Email *" value={form.email} onChange={set("email")} className="input-elegant sm:col-span-2" />
                <input data-testid="checkout-input-first-name" type="text" required placeholder="Prénom *" value={form.first_name} onChange={set("first_name")} className="input-elegant" />
                <input data-testid="checkout-input-last-name" type="text" required placeholder="Nom *" value={form.last_name} onChange={set("last_name")} className="input-elegant" />
                <input data-testid="checkout-input-phone" type="tel" required placeholder="Téléphone (suivi SMS) *" value={form.phone} onChange={set("phone")} className="input-elegant sm:col-span-2" />
              </div>
            </section>

            {/* Step 2 */}
            <section className="bg-white rounded-3xl border border-blush-deep p-6 sm:p-8" data-testid="checkout-step-address">
              <p className="font-mono text-xs tracking-widest uppercase text-terracotta mb-6 flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-terracotta text-cream flex items-center justify-center text-[11px]">2</span>
                Adresse de livraison
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="checkout-input-address" type="text" required placeholder="Adresse (numéro et rue) *" value={form.address} onChange={set("address")} className="input-elegant sm:col-span-2" />
                <input data-testid="checkout-input-city" type="text" required placeholder="Ville *" value={form.city} onChange={set("city")} className="input-elegant" />
                <input data-testid="checkout-input-zip" type="text" required placeholder="Code postal *" value={form.zip} onChange={set("zip")} className="input-elegant" />
                <select data-testid="checkout-input-country" value={form.country} onChange={set("country")} className="input-elegant sm:col-span-2">
                  {["France", "Belgique", "Suisse", "Luxembourg"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </section>

            {/* Step 3 */}
            <section className="bg-white rounded-3xl border border-blush-deep p-6 sm:p-8" data-testid="checkout-step-shipping">
              <p className="font-mono text-xs tracking-widest uppercase text-terracotta mb-6 flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-terracotta text-cream flex items-center justify-center text-[11px]">3</span>
                Mode de livraison
              </p>
              {subtotal >= FREE_SHIPPING_THRESHOLD ? (
                <div data-testid="checkout-free-shipping" className="rounded-2xl border border-[#2E6B4F]/30 bg-[#2E6B4F]/5 px-5 py-4 flex items-center gap-3">
                  <Truck size={18} className="text-[#2E6B4F]" />
                  <p className="text-sm font-medium text-[#2E6B4F]">Livraison offerte ✦ votre commande dépasse {formatPrice(FREE_SHIPPING_THRESHOLD)}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {SHIPPING_OPTIONS.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`checkout-shipping-${s.id}`}
                      onClick={() => setShipping(s.id)}
                      className={`w-full flex items-center justify-between rounded-2xl border px-5 py-4 transition-all ${
                        shipping === s.id ? "border-terracotta bg-blush/60" : "border-blush-deep hover:border-rose-gold"
                      }`}
                    >
                      <span className="flex items-center gap-3 text-left">
                        {s.id === "express" ? <Zap size={17} className="text-terracotta" /> : <Truck size={17} className="text-terracotta" />}
                        <span>
                          <span className="block text-sm font-semibold text-onyx">{s.label}</span>
                          <span className="block text-xs text-muted-warm">{s.detail}</span>
                        </span>
                      </span>
                      <span className="font-serif text-lg text-terracotta">{formatPrice(s.price)}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Step 4 */}
            <section className="bg-white rounded-3xl border border-blush-deep p-6 sm:p-8" data-testid="checkout-step-payment">
              <p className="font-mono text-xs tracking-widest uppercase text-terracotta mb-6 flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-terracotta text-cream flex items-center justify-center text-[11px]">4</span>
                Paiement sécurisé
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  type="button"
                  data-testid="checkout-method-card"
                  onClick={() => setMethod("card")}
                  className={`rounded-2xl border px-4 py-4 flex items-center justify-center gap-2 transition-all ${
                    method === "card" ? "border-terracotta bg-blush/60 shadow-sm" : "border-blush-deep hover:border-rose-gold"
                  }`}
                >
                  <CreditCard size={17} className="text-onyx" />
                  <span className="text-sm font-semibold text-onyx">Carte Bleue</span>
                </button>
                <button
                  type="button"
                  data-testid="checkout-method-paypal"
                  onClick={() => setMethod("paypal")}
                  className={`rounded-2xl border px-4 py-4 flex items-center justify-center transition-all ${
                    method === "paypal" ? "border-terracotta bg-blush/60 shadow-sm" : "border-blush-deep hover:border-rose-gold"
                  }`}
                >
                  <PayPalMark />
                </button>
              </div>
              {method === "card" ? (
                <div className="rounded-2xl bg-cream border border-blush-deep px-5 py-4 mb-6">
                  <p className="text-xs text-muted-warm leading-relaxed">
                    Vous serez redirigée vers la page de paiement sécurisée <span className="font-semibold text-onyx">Stripe</span> (Visa, Mastercard, CB). Vos données bancaires ne transitent jamais par notre site.
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl bg-cream border border-blush-deep px-5 py-4 mb-6">
                  <p className="text-xs text-muted-warm leading-relaxed">
                    Paiement via votre compte <PayPalMark /> — <span className="font-semibold text-terracotta">mode démonstration</span> : la commande sera confirmée immédiatement, sans débit réel.
                  </p>
                </div>
              )}
              <button
                type="submit"
                data-testid="checkout-pay-button"
                disabled={loading}
                className="w-full rounded-full bg-terracotta text-cream py-4.5 py-4 text-sm font-semibold tracking-wide hover:bg-onyx transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin" /> Redirection sécurisée…</>
                ) : method === "card" ? (
                  <><Lock size={15} /> Payer {formatPrice(total)} par carte</>
                ) : (
                  <>Payer {formatPrice(total)} avec <PayPalMark /></>
                )}
              </button>
              <p className="mt-4 text-center text-[11px] text-muted-warm flex items-center justify-center gap-1.5">
                <Lock size={11} /> Transactions chiffrées SSL — vos données sont protégées
              </p>
            </section>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-2">
            <div className="lg:sticky lg:top-28 bg-white rounded-3xl border border-blush-deep p-6 sm:p-8" data-testid="checkout-summary">
              <p className="font-mono text-xs tracking-widest uppercase text-terracotta mb-6">Votre commande</p>
              <div className="space-y-4 mb-6">
                {cartLines.map(({ variant, qty }) => (
                  <div key={variant.id} className="flex items-center gap-3" data-testid={`checkout-summary-item-${variant.id}`}>
                    <div className="relative">
                      <img src={variant.image} alt={variant.label} className="w-14 h-16 object-cover rounded-xl" />
                      <span className="absolute -top-2 -right-2 bg-onyx text-cream text-[10px] font-mono w-5 h-5 rounded-full flex items-center justify-center">{qty}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-onyx">{variant.label}</p>
                      <p className="text-xs text-muted-warm">{variant.productName}</p>
                    </div>
                    <p className="text-sm font-semibold text-onyx">{formatPrice(variant.price * qty)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-blush-deep pt-4 mb-4">
                {promo ? (
                  <div data-testid="checkout-promo-applied" className="flex items-center justify-between rounded-xl bg-[#2E6B4F]/5 border border-[#2E6B4F]/25 px-4 py-2.5">
                    <span className="text-xs font-mono font-bold text-[#2E6B4F]">{promo.code} · -{promo.percent}%</span>
                    <button type="button" data-testid="checkout-promo-remove" onClick={() => setPromo(null)} className="text-stone-400 hover:text-red-500 transition-colors"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="checkout-promo-input"
                      type="text"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="Code promo (ex. BIENVENUE10)"
                      className="input-elegant flex-1 !py-2.5 text-xs"
                    />
                    <button
                      type="button"
                      data-testid="checkout-promo-apply"
                      onClick={applyPromo}
                      disabled={promoLoading}
                      className="rounded-xl bg-onyx text-cream px-4 text-xs font-semibold hover:bg-terracotta transition-colors disabled:opacity-60"
                    >
                      {promoLoading ? "…" : "Appliquer"}
                    </button>
                  </div>
                )}
              </div>
              <div className="border-t border-blush-deep pt-4 space-y-2.5 text-sm">
                <div className="flex justify-between text-muted-warm">
                  <span>Sous-total</span>
                  <span data-testid="checkout-summary-subtotal">{formatPrice(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-[#2E6B4F]">
                    <span>Code {promo.code} (-{promo.percent}%)</span>
                    <span data-testid="checkout-summary-discount">− {formatPrice(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-muted-warm">
                  <span>Livraison</span>
                  <span data-testid="checkout-summary-shipping" className={shippingFee === 0 ? "text-[#2E6B4F] font-medium" : ""}>
                    {shippingFee === 0 ? "Offerte" : formatPrice(shippingFee)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline pt-3 border-t border-blush-deep">
                  <span className="font-semibold text-onyx">Total TTC</span>
                  <span data-testid="checkout-summary-total" className="font-serif text-3xl text-terracotta">{formatPrice(total)}</span>
                </div>
              </div>
              <button
                type="button"
                data-testid="checkout-back-to-cart"
                onClick={() => navigate("/produit")}
                className="mt-6 w-full text-center text-xs text-muted-warm hover:text-terracotta transition-colors flex items-center justify-center gap-1"
              >
                Continuer mes achats <ChevronRight size={12} />
              </button>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
